"use client";
import { useTranslations } from "next-intl";
import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import InputDataContent from "../inputDataContent";
import { cn } from "@/lib";
import { routes } from "@/routes";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import { useRef } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import {
  ICompanyMember,
  IRawPurchasedCompanyLicensesInquiryResData,
} from "@/interfaces/inquiries/legal";

const CompanyLicensesInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedCompanyLicensesInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const companyMembers = data.data.data.members;
  const { national_code } = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("companyInformationInquiry.nationalId"),
      value: national_code,
    },
  ];

  function transformData(
    data: ICompanyMember[],
    excludedKeys: string[] = ["typeCode", "positionId"],
  ): IBaseLabelValue[][] {
    return data.map((item) =>
      (Object.keys(item) as (keyof ICompanyMember)[])
        .filter((key) => !excludedKeys.includes(key as string))
        .map((key) => ({
          label: t(`companyMembersInquiry.${String(key)}` as any),
          value: String(item[key] ?? ""),
        })),
    );
  }

  const resultContent: IBaseLabelValue[][] = transformData(companyMembers);

  const flattenedResultContent: IBaseLabelValue[] = resultContent.flat();
  const pdfContentRef = useRef<HTMLDivElement>(null);
  return (
    <div className="shadow-main from-sunset-gold relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="w-full items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("companyLicensesInquiry.title")}
        </h2>
        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-4 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("companyInformationInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="company-licenses-inquiry-download-pdf"
            resultContent={flattenedResultContent}
            fileName={`گزارش مجوزهای شرکت ${national_code}`}
            tableTitle={`گزارش مجوزهای شرکت ${national_code}`}
          />
        </FlexBetween>
        {resultContent.map((item, index) => (
          <ul
            key={index}
            className="shadow-main mb-8 flex flex-col gap-4 rounded-xl p-4 py-8 sm:p-8"
          >
            {item.map((member, i) => (
              <li
                key={i}
                className="flex-between flex items-start text-xs sm:text-sm lg:text-base"
              >
                <span className="min-w-max">{member.label}</span>
                <p className="text-primary-main text-justify">{member.value}</p>
              </li>
            ))}
          </ul>
        ))}
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="company-licenses-inquiry-download-pdf"
            resultContent={flattenedResultContent}
            fileName={`گزارش مجوزهای شرکت ${national_code}`}
            tableTitle={`گزارش مجوزهای شرکت${national_code}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-company-licenses-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-company-licenses-inquiry-new-request"
              onClick={() =>
                router.push(
                  routes.services.Company_Licenses_Inquiry.rewritePath,
                )
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle={`گزارش مجوزهای شرکت${national_code}`}
          resultContent={flattenedResultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default CompanyLicensesInquiryDetails;
